'use client'

import React from 'react'

import {
  OB,
  FONT_SANS,
  FONT_MONO,
  Icon,
  Sparkle,
  PaprMark,
  Spinner,
  OnbField,
  OnbInput,
  OnbSelect,
  OnbPrimary,
  OnbGhost,
  OnbSelectCard,
  OnbChip,
  OnbTag,
} from './kit'

// Unified auth surface — shares the registration brand panel across every screen.
// Modes: signin · register · verify · forgot · reset-sent · set-new · reset-done
// Reuses RegBrandPanel, AccountStep, VerifyStep, Spinner (from onb-registration.jsx).

type AuthMode = 'signin' | 'register' | 'verify' | 'forgot' | 'reset-sent' | 'set-new' | 'reset-done'

/**
 * Split-screen auth layout: dark brand panel + form column with a top-right link slot.
 * Shared by the preview AuthFlow and the live per-route auth pages. `stepKey` keys the
 * form wrapper so the entrance animation replays on screen change.
 */
export function AuthSplit({ topRight, stepKey, children }: { topRight?: React.ReactNode; stepKey?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', width: '100%', height: '100%', background: OB.bg, fontFamily: FONT_SANS }}>
      <RegBrandPanel />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '22px 32px', gap: 6, fontSize: 13, color: OB.mute, minHeight: 64 }}>
          {topRight}
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 32px 40px' }}>
          <div key={stepKey} className="onb-step" style={{ width: 400, maxWidth: '100%' }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

export function AuthFlow({
  initialMode = 'signin',
  onAuthed,
}: {
  initialMode?: AuthMode
  onAuthed: (u: { name: string; email: string }) => void
}) {
  const [mode, setMode] = React.useState<AuthMode>(initialMode)
  // shared field state (used by register + signin + reset)
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [pw, setPw] = React.useState('')
  const [method, setMethod] = React.useState('link')
  const [busy, setBusy] = React.useState(false)

  const emailOk = /.+@.+\..+/.test(email)
  const canContinue = name.trim().length > 1 && emailOk && (method === 'link' || pw.length >= 6)
  const firstName = name.trim().split(' ')[0] || 'there'

  function submitAccount() {
    if (!canContinue) return
    setBusy(true)
    setTimeout(() => { setBusy(false); method === 'link' ? setMode('verify') : onAuthed({ name: name.trim(), email }) }, 650)
  }

  const topLinks: Record<AuthMode, { q: string; a: string; go: AuthMode } | null> = {
    signin:        { q: 'New to Papr?', a: 'Create an account', go: 'register' },
    register:      { q: 'Already with us?', a: 'Sign in', go: 'signin' },
    verify:        { q: 'Wrong email?', a: 'Start over', go: initialMode },
    forgot:        { q: 'Remembered it?', a: 'Back to sign in', go: 'signin' },
    'reset-sent':  { q: 'Remembered it?', a: 'Back to sign in', go: 'signin' },
    'set-new':     null,
    'reset-done':  null,
  }
  const topLink = topLinks[mode]

  return (
    <AuthSplit
      stepKey={mode}
      topRight={topLink && (
        <>
          <span>{topLink.q}</span>
          <a href="#" onClick={(e) => { e.preventDefault(); setMode(topLink.go) }} style={{ color: OB.ink, fontWeight: 600, textDecoration: 'none' }}>{topLink.a}</a>
        </>
      )}
    >
      {mode === 'register' && <AccountStep {...{ name, setName, email, setEmail, pw, setPw, method, setMethod, busy, canContinue, submitAccount }}/>}
      {mode === 'signin' && <SignIn {...{ email, setEmail, pw, setPw, busy, setBusy }}
        onSignedIn={() => onAuthed({ name: name.trim() || 'Alex Rivera', email: email || 'alex@studio.com' })}
        onForgot={() => setMode('forgot')} onMagic={() => setMode('verify')}/>}
      {mode === 'verify' && <VerifyStep email={email || 'you@studio.com'} firstName={firstName}
        onBack={() => setMode(initialMode)} onVerified={() => onAuthed({ name: name.trim() || 'Alex Rivera', email: email || 'alex@studio.com' })}/>}
      {mode === 'forgot' && <ForgotPassword {...{ email, setEmail, busy, setBusy }} onSent={() => setMode('reset-sent')}/>}
      {mode === 'reset-sent' && <ResetLinkSent email={email || 'you@studio.com'} onOpen={() => setMode('set-new')} onResend={() => {}}/>}
      {mode === 'set-new' && <SetNewPassword onDone={() => setMode('reset-done')}/>}
      {mode === 'reset-done' && <ResetDone onSignIn={() => { setPw(''); setMode('signin') }}/>}
    </AuthSplit>
  )
}

// ---- Sign in ----
function SignIn({ email, setEmail, pw, setPw, busy, setBusy, onSignedIn, onForgot, onMagic }: {
  email: string
  setEmail: (v: string) => void
  pw: string
  setPw: (v: string) => void
  busy: boolean
  setBusy: (v: boolean) => void
  onSignedIn: () => void
  onForgot: () => void
  onMagic: () => void
}) {
  const emailOk = /.+@.+\..+/.test(email)
  const canSubmit = emailOk && pw.length >= 1
  function submit() {
    if (!canSubmit) return
    setBusy(true)
    setTimeout(() => { setBusy(false); onSignedIn() }, 700)
  }
  return (
    <div>
      <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.035em', color: OB.ink, margin: 0, lineHeight: 1.08 }}>Welcome back</h1>
      <p style={{ fontSize: 14.5, color: OB.mute, margin: '11px 0 26px', lineHeight: 1.5 }}>Sign in to pick up where you left off.</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        <OnbField label="Work email">
          <OnbInput value={email} onChange={setEmail} placeholder="you@studio.com" type="email" autoFocus size="lg"
            onKeyDown={e => { if (e.key === 'Enter') submit() }}/>
        </OnbField>
        <OnbField label="Password" hint={<a href="#" onClick={e => { e.preventDefault(); onForgot() }} style={{ color: OB.ink, fontWeight: 600, textDecoration: 'none' }}>Forgot?</a>}>
          <OnbInput value={pw} onChange={setPw} placeholder="••••••••" type="password" size="lg"
            onKeyDown={e => { if (e.key === 'Enter') submit() }}/>
        </OnbField>
        <OnbPrimary full size="lg" arrow={!busy} onClick={submit} disabled={!canSubmit || busy}>
          {busy ? <Spinner/> : 'Sign in'}
        </OnbPrimary>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
        <span style={{ flex: 1, height: 1, background: OB.line2 }}></span>
        <span style={{ fontSize: 11.5, color: OB.dim, fontFamily: FONT_MONO }}>or</span>
        <span style={{ flex: 1, height: 1, background: OB.line2 }}></span>
      </div>

      <OnbGhost size="lg" onClick={onMagic}>
        <Icon d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" size={17} color={OB.ink}/>
        Email me a sign-in link
      </OnbGhost>
      <div style={{ height: 12 }}></div>
      <p style={{ fontSize: 11.5, color: OB.dim, margin: 0, lineHeight: 1.55, textAlign: 'center' }}>
        Demo · any email + password signs in.
      </p>
    </div>
  )
}

// ---- Forgot password (request reset) ----
function ForgotPassword({ email, setEmail, busy, setBusy, onSent }: {
  email: string
  setEmail: (v: string) => void
  busy: boolean
  setBusy: (v: boolean) => void
  onSent: () => void
}) {
  const emailOk = /.+@.+\..+/.test(email)
  function submit() {
    if (!emailOk) return
    setBusy(true)
    setTimeout(() => { setBusy(false); onSent() }, 700)
  }
  return (
    <div>
      <div style={{ width: 52, height: 52, borderRadius: 15, background: OB.ink, marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon d="M12 1a5 5 0 00-5 5v3H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2v-9a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm3 8H9V6a3 3 0 016 0v3z" size={24} color={OB.lime}/>
      </div>
      <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.03em', color: OB.ink, margin: 0 }}>Reset your password</h1>
      <p style={{ fontSize: 14, color: OB.mute, margin: '11px 0 24px', lineHeight: 1.55 }}>
        Enter the email you signed up with and we’ll send a secure link to set a new password.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        <OnbField label="Work email">
          <OnbInput value={email} onChange={setEmail} placeholder="you@studio.com" type="email" autoFocus size="lg"
            onKeyDown={e => { if (e.key === 'Enter') submit() }}/>
        </OnbField>
        <OnbPrimary full size="lg" arrow={!busy} onClick={submit} disabled={!emailOk || busy}>
          {busy ? <Spinner/> : 'Send reset link'}
        </OnbPrimary>
      </div>
    </div>
  )
}

// ---- Reset link sent (check inbox) ----
function ResetLinkSent({ email, onOpen, onResend }: { email: string; onOpen: () => void; onResend: () => void }) {
  const [sent, setSent] = React.useState(false)
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, borderRadius: 16, background: OB.ink, margin: '0 auto 22px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" size={26} color={OB.lime}/>
      </div>
      <h1 style={{ fontSize: 27, fontWeight: 600, letterSpacing: '-0.03em', color: OB.ink, margin: 0 }}>Check your inbox</h1>
      <p style={{ fontSize: 14, color: OB.mute, margin: '11px 0 24px', lineHeight: 1.55 }}>
        We sent a password reset link to <strong style={{ color: OB.ink, fontWeight: 600 }}>{email}</strong>. It expires in 30 minutes.
      </p>
      <OnbPrimary full size="lg" onClick={onOpen}>Open reset link</OnbPrimary>
      <div style={{ marginTop: 16, fontSize: 12.5, color: OB.dim }}>
        Didn’t get it?{' '}
        <a href="#" onClick={e => { e.preventDefault(); setSent(true) }} style={{ color: OB.ink, fontWeight: 600, textDecoration: 'none' }}>Resend email</a>
        {sent && <span style={{ color: OB.greenInk, marginLeft: 8, fontFamily: FONT_MONO }}>✓ sent</span>}
      </div>
      <div style={{ marginTop: 26, padding: '10px 14px', borderRadius: 10, background: 'rgba(0,0,0,0.03)', border: `1px dashed ${OB.line2}`, fontSize: 11.5, color: OB.dim, fontFamily: FONT_MONO }}>
        Demo · “Open reset link” continues the flow
      </div>
    </div>
  )
}

// ---- Set new password ----
function SetNewPassword({ onDone }: { onDone: () => void }) {
  const [pw, setPw] = React.useState('')
  const [confirm, setConfirm] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const reqs = [
    { label: 'At least 8 characters', ok: pw.length >= 8 },
    { label: 'One uppercase letter', ok: /[A-Z]/.test(pw) },
    { label: 'One number', ok: /\d/.test(pw) },
  ]
  const score = reqs.filter((r: { label: string; ok: boolean }) => r.ok).length
  const match = confirm.length > 0 && pw === confirm
  const canSave = score === 3 && match
  const strength = ['Weak', 'Weak', 'Fair', 'Strong'][score]
  const strengthColor = ['#ef4444', '#ef4444', '#f59e0b', OB.greenInk][score]
  function save() {
    if (!canSave) return
    setBusy(true)
    setTimeout(() => { setBusy(false); onDone() }, 700)
  }
  return (
    <div>
      <div style={{ width: 52, height: 52, borderRadius: 15, background: OB.ink, marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon d="M12 1a5 5 0 00-5 5v3H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2v-9a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm3 8H9V6a3 3 0 016 0v3z" size={24} color={OB.lime}/>
      </div>
      <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.03em', color: OB.ink, margin: 0 }}>Set a new password</h1>
      <p style={{ fontSize: 14, color: OB.mute, margin: '11px 0 24px', lineHeight: 1.55 }}>Choose a strong password you don’t use anywhere else.</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        <OnbField label="New password">
          <OnbInput value={pw} onChange={setPw} placeholder="••••••••" type="password" autoFocus size="lg"/>
        </OnbField>
        {pw.length > 0 && (
          <div>
            <div style={{ display: 'flex', gap: 5, marginBottom: 9 }}>
              {[0, 1, 2].map((i: number) => (
                <span key={i} style={{ flex: 1, height: 4, borderRadius: 999, background: i < score ? strengthColor : 'rgba(0,0,0,0.08)', transition: 'background .2s' }}></span>
              ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 14px' }}>
              {reqs.map((r: { label: string; ok: boolean }, i: number) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: r.ok ? OB.greenInk : OB.dim }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', background: r.ok ? 'rgba(16,185,129,0.14)' : 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {r.ok && <Icon d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" size={9} color={OB.greenInk}/>}
                  </span>{r.label}
                </span>
              ))}
            </div>
          </div>
        )}
        <OnbField label="Confirm password">
          <OnbInput value={confirm} onChange={setConfirm} placeholder="••••••••" type="password" size="lg"
            onKeyDown={e => { if (e.key === 'Enter') save() }}/>
        </OnbField>
        {confirm.length > 0 && !match && <div style={{ fontSize: 11.5, color: '#ef4444', marginTop: -6 }}>Passwords don’t match yet.</div>}
        <OnbPrimary full size="lg" arrow={!busy} onClick={save} disabled={!canSave || busy}>
          {busy ? <Spinner/> : 'Save new password'}
        </OnbPrimary>
      </div>
    </div>
  )
}

// ---- Reset done ----
function ResetDone({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, borderRadius: 16, background: OB.green, margin: '0 auto 22px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" size={28} color="#fff"/>
      </div>
      <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.03em', color: OB.ink, margin: 0 }}>Password updated</h1>
      <p style={{ fontSize: 14, color: OB.mute, margin: '11px 0 24px', lineHeight: 1.55 }}>
        You’re all set. Sign in with your new password to get back to work.
      </p>
      <OnbPrimary full size="lg" arrow onClick={onSignIn}>Continue to sign in</OnbPrimary>
    </div>
  )
}

// --- Account sub-step ---
function AccountStep({ name, setName, email, setEmail, pw, setPw, method, setMethod, busy, canContinue, submitAccount }: {
  name: string
  setName: (v: string) => void
  email: string
  setEmail: (v: string) => void
  pw: string
  setPw: (v: string) => void
  method: string
  setMethod: (v: string) => void
  busy: boolean
  canContinue: boolean
  submitAccount: () => void
}) {
  return (
    <div>
      <div style={{ display: 'inline-flex', marginBottom: 22 }}>
        <OnbTag color={OB.ink} bg="rgba(202,255,51,0.5)">Free · no card needed</OnbTag>
      </div>
      <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.035em', color: OB.ink, margin: 0, lineHeight: 1.08 }}>Create your account</h1>
      <p style={{ fontSize: 14.5, color: OB.mute, margin: '11px 0 26px', lineHeight: 1.5 }}>
        Start sending invoices in minutes. Papr drafts, sends, and chases them so you don’t have to.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        <OnbField label="Your name">
          <OnbInput value={name} onChange={setName} placeholder="Alex Rivera" autoFocus size="lg"/>
        </OnbField>
        <OnbField label="Work email">
          <OnbInput value={email} onChange={setEmail} placeholder="you@studio.com" type="email" size="lg"
            onKeyDown={e => { if (e.key === 'Enter' && method === 'link') submitAccount() }}/>
        </OnbField>

        {/* Method segmented control */}
        <div style={{ display: 'flex', gap: 6, padding: 4, background: 'rgba(0,0,0,0.04)', borderRadius: 12 }}>
          {[
            { k: 'link', label: 'Email me a link', sub: 'Fastest' },
            { k: 'password', label: 'Set a password', sub: null },
          ].map((o: { k: string; label: string; sub: string | null }) => {
            const on = method === o.k
            return (
              <button key={o.k} onClick={() => setMethod(o.k)} className="onb-seg"
                style={{ flex: 1, height: 42, borderRadius: 9, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  background: on ? '#fff' : 'transparent', boxShadow: on ? '0 1px 0 rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.05)' : 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  fontSize: 13, fontWeight: on ? 600 : 500, color: on ? OB.ink : OB.mute }}>
                {o.label}
                {o.sub && on && <span style={{ fontSize: 10, color: OB.greenInk, background: 'rgba(16,185,129,0.12)', padding: '2px 6px', borderRadius: 999, fontWeight: 600, fontFamily: FONT_MONO }}>{o.sub}</span>}
              </button>
            )
          })}
        </div>

        {method === 'password' && (
          <div className="onb-step">
            <OnbField label="Password" hint="At least 6 characters">
              <OnbInput value={pw} onChange={setPw} placeholder="••••••••" type="password" size="lg"
                onKeyDown={e => { if (e.key === 'Enter') submitAccount() }}/>
            </OnbField>
          </div>
        )}

        <OnbPrimary full size="lg" arrow={!busy} onClick={submitAccount} disabled={!canContinue || busy}>
          {busy ? <Spinner/> : (method === 'link' ? 'Email me a sign-in link' : 'Create account')}
        </OnbPrimary>
      </div>

      <p style={{ fontSize: 11.5, color: OB.dim, margin: '18px 0 0', lineHeight: 1.55, textAlign: 'center' }}>
        By continuing you agree to Papr’s <a href="/terms" style={{ color: OB.mute }}>Terms</a> &amp; <a href="/privacy" style={{ color: OB.mute }}>Privacy Policy</a>.
      </p>
    </div>
  )
}

// --- Verify (6-digit code) sub-step ---
// Mock mode (preview): any 6 digits "verify". Live mode: pass `onVerify(code)` (awaited;
// throw to show an inline error) and optionally `onResend()`. `demoHint={false}` hides the
// preview-only hint. `successText` lets the live caller match the post-verify copy.
export function VerifyStep({
  email,
  firstName,
  onBack,
  onVerified,
  onVerify,
  onResend,
  demoHint = true,
  successText,
}: {
  email: string
  firstName: string
  onBack: () => void
  onVerified: () => void
  onVerify?: (code: string) => Promise<void>
  onResend?: () => Promise<void>
  demoHint?: boolean
  successText?: string
}) {
  const [code, setCode] = React.useState(['', '', '', '', '', ''])
  const [resendIn, setResendIn] = React.useState(30)
  const [ok, setOk] = React.useState(false)
  const [verifying, setVerifying] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [resent, setResent] = React.useState(false)
  const refs = React.useRef<(HTMLInputElement | null)[]>([])

  React.useEffect(() => {
    if (resendIn <= 0) { setResent(false); return }
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [resendIn])

  async function submit(full: string) {
    setError(null)
    if (onVerify) {
      setVerifying(true)
      try {
        await onVerify(full)
        setOk(true)
        setTimeout(() => onVerified && onVerified(), 600)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'That code didn’t work. Try again.')
        setCode(['', '', '', '', '', ''])
        refs.current[0] && refs.current[0]!.focus()
      } finally {
        setVerifying(false)
      }
    } else {
      setOk(true)
      setTimeout(() => onVerified && onVerified(), 720)
    }
  }

  function setDigit(i: number, v: string) {
    v = v.replace(/\D/g, '').slice(-1)
    const next = code.slice(); next[i] = v; setCode(next)
    if (v && i < 5) refs.current[i + 1] && refs.current[i + 1]!.focus()
    if (next.every((d: string) => d !== '')) void submit(next.join(''))
  }
  function onKey(i: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !code[i] && i > 0) refs.current[i - 1] && refs.current[i - 1]!.focus()
  }
  function paste(e: React.ClipboardEvent) {
    const txt = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6)
    if (txt.length) {
      e.preventDefault()
      const next = txt.split('').concat(['', '', '', '', '', '']).slice(0, 6)
      setCode(next)
      if (next.every((d: string) => d !== '')) void submit(next.join(''))
      else refs.current[txt.length] && refs.current[txt.length]!.focus()
    }
  }

  async function resend() {
    setError(null)
    setResendIn(30)
    setResent(true)
    if (onResend) {
      try {
        await onResend()
      } catch {
        /* surface nothing — the countdown already restarted */
      }
    }
  }

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, borderRadius: 16, background: ok ? OB.green : OB.ink, margin: '0 auto 22px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background .3s' }}>
        {ok
          ? <Icon d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" size={26} color="#fff"/>
          : verifying
            ? <Spinner color="#fff" size={24}/>
            : <Icon d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" size={26} color={OB.lime}/>}
      </div>
      <h1 style={{ fontSize: 27, fontWeight: 600, letterSpacing: '-0.03em', color: OB.ink, margin: 0 }}>
        {ok ? `Welcome, ${firstName}!` : 'Check your inbox'}
      </h1>
      <p style={{ fontSize: 14, color: OB.mute, margin: '11px 0 24px', lineHeight: 1.55 }}>
        {ok ? (successText || 'Verified — setting up your workspace…') : <>We sent a 6-digit code to <strong style={{ color: OB.ink, fontWeight: 600 }}>{email}</strong>. Enter it below.</>}
      </p>

      {!ok && (
        <>
          <div style={{ display: 'flex', gap: 9, justifyContent: 'center', marginBottom: error ? 10 : 18 }} onPaste={paste}>
            {code.map((d: string, i: number) => (
              <input key={i} ref={el => { refs.current[i] = el }} value={d}
                inputMode="numeric" maxLength={1} disabled={verifying}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDigit(i, e.target.value)} onKeyDown={(e: React.KeyboardEvent) => onKey(i, e)}
                autoFocus={i === 0} className="onb-code"
                style={{ width: 48, height: 58, textAlign: 'center', fontSize: 24, fontWeight: 600, fontFamily: FONT_MONO,
                  color: OB.ink, border: `1.5px solid ${error ? '#ef4444' : d ? OB.ink : OB.line2}`, borderRadius: 12, background: '#fff', outline: 'none' }}/>
            ))}
          </div>
          {error && <div style={{ fontSize: 12, color: '#b91c1c', marginBottom: 14 }}>{error}</div>}
          <div style={{ fontSize: 12.5, color: OB.dim, fontFamily: FONT_MONO, marginBottom: 22 }}>
            {resent && resendIn > 0
              ? <span style={{ color: OB.ink }}>New code sent — check your inbox and spam folder.</span>
              : resendIn > 0
                ? <>Didn’t get it? Check spam, or resend in 0:{String(resendIn).padStart(2, '0')}</>
                : <a href="#" onClick={e => { e.preventDefault(); void resend() }} style={{ color: OB.ink, fontWeight: 600 }}>Resend code</a>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 12.5 }}>
            <a href="#" onClick={e => { e.preventDefault(); onBack() }} style={{ color: OB.mute, textDecoration: 'none', fontWeight: 500 }}>← Use a different email</a>
          </div>
          {demoHint && (
            <div style={{ marginTop: 26, padding: '10px 14px', borderRadius: 10, background: 'rgba(0,0,0,0.03)', border: `1px dashed ${OB.line2}`, fontSize: 11.5, color: OB.dim, fontFamily: FONT_MONO }}>
              Demo · type any 6 digits to continue
            </div>
          )}
        </>
      )}
    </div>
  )
}

// --- Left brand panel ---
export function RegBrandPanel() {
  return (
    <div className="papr-brand-panel" style={{ width: '44%', maxWidth: 560, flexShrink: 0, background: OB.ink, color: '#fff', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '32px 40px 36px' }}>
      {/* warm glow */}
      <div style={{ position: 'absolute', top: -120, right: -100, width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(202,255,51,0.16), transparent 65%)', pointerEvents: 'none' }}></div>
      <div style={{ position: 'absolute', bottom: -140, left: -120, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,92,255,0.14), transparent 65%)', pointerEvents: 'none' }}></div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <PaprMark accent="#fff" dot={OB.lime} size={24}/>
      </div>

      <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 380 }}>
        <h2 style={{ fontSize: 34, fontWeight: 600, letterSpacing: '-0.035em', lineHeight: 1.12, margin: 0 }}>
          Get paid, <span style={{ color: OB.lime }}>without the chasing.</span>
        </h2>
        <p style={{ fontSize: 14.5, color: 'rgba(255,255,255,0.62)', lineHeight: 1.6, margin: '16px 0 28px' }}>
          Papr is the invoicing tool that does the busywork — drafting, sending, and following up — so cash lands on time.
        </p>

        {/* floating product card */}
        <div className="onb-float" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 16, padding: '16px 18px', backdropFilter: 'blur(8px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 14 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: '#0ea5e9', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>FN</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Field Notes Co.</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontFamily: FONT_MONO }}>INV-2026-0143</div>
            </div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, background: 'rgba(16,185,129,0.18)', color: '#6ee7b7', fontSize: 11, fontWeight: 600 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }}></span> Paid
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.09em', fontWeight: 600 }}>Amount</div>
              <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', marginTop: 3 }}>€8,253.41</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, color: '#6ee7b7', fontFamily: FONT_MONO }}>
              <Sparkle size={12} color={OB.lime}/> Paid 3 days early
            </div>
          </div>
        </div>

        {/* value bullets */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 13, marginTop: 28 }}>
          {[
            'AI drafts invoices from your time & contracts',
            'Automatic, polite reminders chase late payers',
            'Online card & bank payments, paid 2× faster',
          ].map((t: string, i: number) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 11, fontSize: 13.5, color: 'rgba(255,255,255,0.82)' }}>
              <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(202,255,51,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" size={12} color={OB.lime}/>
              </span>
              {t}
            </div>
          ))}
        </div>
      </div>

      {/* social proof */}
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex' }}>
          {['#f59e0b', '#0ea5e9', '#7c5cff', '#10b981'].map((c: string, i: number) => (
            <div key={i} style={{ width: 28, height: 28, borderRadius: '50%', background: c, border: '2px solid #0a0a0b', marginLeft: i ? -9 : 0 }}></div>
          ))}
        </div>
        <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.62)' }}>
          Trusted by <strong style={{ color: '#fff', fontWeight: 600 }}>12,000+</strong> freelancers &amp; small businesses
        </div>
      </div>
    </div>
  )
}
